/**
 * AudioProcessor — inert design-clone implementation.
 *
 * Upstream this built a Web Audio chain over the local mic stream:
 *   source → highpass → lowpass → noise gate → normalizer → limiter →
 *   outputGain → destination
 *
 * There is no mic stream in this clone (see `services/webRTCManager.ts`), so
 * there is nothing to filter. The class is kept because `App.tsx` and
 * `CallLayout` construct one and drive it from the audio-settings panel —
 * every slider and toggle in that panel still moves, still persists to the
 * call store, and still round-trips through the profile row. Only the DSP is
 * gone.
 *
 * Values are retained rather than discarded so the panel can read back what
 * it wrote; `getSettings()` exists for debugging that.
 */

export class AudioProcessor {
  private noiseSuppressionEnabled = true;
  private normalizeThreshold = -24;
  private limiterThreshold = -1;
  private outputGain = 1.0;

  /**
   * Upstream: returned a processed clone of the input stream. Here the input
   * is passed straight through, so callers that swap in the "processed"
   * stream keep working on the fixture stream unchanged.
   */
  public processStream(stream: MediaStream): MediaStream {
    return stream;
  }

  /** No destination node exists to read back. */
  public getProcessedStream(): MediaStream | null {
    return null;
  }

  public setNoiseSuppressionEnabled(enabled: boolean) {
    this.noiseSuppressionEnabled = enabled;
  }

  public setNormalizeThreshold(db: number) {
    this.normalizeThreshold = db;
  }

  public setLimiterThreshold(db: number) {
    this.limiterThreshold = db;
  }

  public setOutputGain(val: number) {
    this.outputGain = val;
  }

  /** Introspection for the audio-settings panel and dev tooling. */
  public getSettings() {
    return {
      noiseSuppressionEnabled: this.noiseSuppressionEnabled,
      normalizeThreshold: this.normalizeThreshold,
      limiterThreshold: this.limiterThreshold,
      outputGain: this.outputGain,
    };
  }

  public close() {}
}

/**
 * Hook-like wrapper for easy use in React components
 */
export function createAudioProcessor() {
  return new AudioProcessor();
}
